import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  X,
  Home as HomeIcon,
  MapPin as LocationIcon,
  DollarSign as MoneyIcon,
  TrendingUp as AssessmentIcon,
  Clock as HistoryIcon,
  Users as PeopleIcon,
  Gavel as GavelIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  resolvePrimaryListing,
  getLatestListingFromListings,
  getResolvedSquareFeet,
} from "../../utils/property-calculations";

// Custom hook for media queries
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
};

const formatCurrency = (value) => {
  if (!value || value === 0) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatNumber = (value) => {
  if (!value || value === 0) return "N/A";
  return new Intl.NumberFormat("en-US").format(value);
};

const PropertyDetailsModal = ({ open, onClose, property }) => {
  const isMobile = useMediaQuery("(max-width: 640px)");

  if (!property) return null;

  const latestFromListings = getLatestListingFromListings(property);

  const renderBasicInfo = () => (
    <Card className="mb-3 sm:mb-4">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <HomeIcon className="h-5 w-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">Basic Information</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="col-span-full">
            <p className="text-sm text-muted-foreground">Address</p>
            <p className="text-sm sm:text-base">
              {property.address || property.streetAddress || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Property Type</p>
            <p className="text-sm sm:text-base">
              {property.propertyType || property.landUse || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Year Built</p>
            <p className="text-sm sm:text-base">
              {property.yearBuilt || property.effectiveYearBuilt || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Square Feet</p>
            <p className="text-sm sm:text-base">
              {(() => {
                const sq = getResolvedSquareFeet(property);
                return sq != null ? `${formatNumber(sq)} sq ft` : "N/A";
              })()}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Lot Size</p>
            <p className="text-sm sm:text-base">
              {property.lotSquareFeet
                ? `${formatNumber(property.lotSquareFeet)} sq ft`
                : property.lotAcres
                ? `${property.lotAcres} acres`
                : "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Bedrooms</p>
            <p className="text-sm sm:text-base">
              {property.bedrooms || latestFromListings?.bedrooms || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Bathrooms</p>
            <p className="text-sm sm:text-base">
              {property.bathrooms ||
                latestFromListings?.bathrooms ||
                (property.fullBathrooms && property.partialBathrooms
                  ? `${property.fullBathrooms}.${property.partialBathrooms}`
                  : property.fullBathrooms || "N/A")}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Pool</p>
            <Badge
              variant={property.pool ? "default" : "secondary"}
              className="mt-1 text-xs sm:text-sm"
            >
              {property.pool ? "Yes" : "No"}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Owner Occupied</p>
            <Badge
              variant={property.ownerOccupied ? "default" : "secondary"}
              className="mt-1 text-xs sm:text-sm"
            >
              {property.ownerOccupied ? "Yes" : "No"}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderValuationInfo = () => (
    <Card className="mb-3 sm:mb-4">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <MoneyIcon className="h-5 w-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">Property Valuation</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Estimated Value</p>
            <p className="text-lg sm:text-xl font-semibold text-primary">
              {formatCurrency(property.estimatedValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Market Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.marketValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Assessed Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.assessedValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Appraised Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.appraisedValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">One Month Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.oneMonthValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Six Month Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.sixMonthValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">One Year Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.oneYearValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Three Year Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.threeYearValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Five Year Value</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.fiveYearValue)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Estimated Equity</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.estimatedEquity)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Estimated Rent</p>
            <p className="text-sm sm:text-base">
              {formatCurrency(property.estimatedRentAmount)} /month
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderOwnershipInfo = () => (
    <Card className="mb-3 sm:mb-4">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <PeopleIcon className="h-5 w-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">
            Ownership Information
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="col-span-full">
            <p className="text-sm text-muted-foreground">Owner Names</p>
            <p className="text-sm sm:text-base">
              {property.ownerNames || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Ownership Type</p>
            <p className="text-sm sm:text-base">
              {property.ownershipType || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Ownership Months</p>
            <p className="text-sm sm:text-base">
              {property.ownershipMonths || "N/A"} months
            </p>
          </div>
          <div className="col-span-full">
            <p className="text-sm text-muted-foreground">Mailing Address</p>
            <p className="text-sm sm:text-base">
              {property.mailingAddress || "N/A"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderSalesHistory = () => (
    <Accordion type="single" collapsible className="border rounded-lg">
      <AccordionItem value="sales-history">
        <AccordionTrigger className="px-4 sm:px-6 hover:no-underline">
          <div className="flex items-center gap-2">
            <HistoryIcon className="h-5 w-5 text-primary" />
            <h3 className="text-base sm:text-lg font-bold">
              Sales History ({property.transactions?.length || 0} transactions)
            </h3>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-2 sm:px-4">
          {property.transactions?.map((transaction, index) => (
            <Card
              key={transaction.id || index}
              className="mb-3 sm:mb-4 bg-muted/50 last:mb-0"
            >
              <CardContent className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Sale Date</p>
                    <p className="text-sm sm:text-base">
                      {formatDate(transaction.saleDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sale Amount</p>
                    <p className="text-base sm:text-xl font-semibold text-primary">
                      {formatCurrency(transaction.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Document Type
                    </p>
                    <p className="text-sm sm:text-base">
                      {transaction.documentType || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Purchase Method
                    </p>
                    <Badge
                      variant={
                        transaction.purchaseMethod === "Cash"
                          ? "default"
                          : "secondary"
                      }
                      className="mt-1 text-xs sm:text-sm"
                    >
                      {transaction.purchaseMethod || "N/A"}
                    </Badge>
                  </div>
                  <div className="col-span-full">
                    <p className="text-sm text-muted-foreground mb-2">
                      Parties
                    </p>
                    <p className="text-sm sm:text-base mb-1">
                      <strong>Buyer:</strong> {transaction.buyerNames || "N/A"}
                    </p>
                    <p className="text-sm sm:text-base">
                      <strong>Seller:</strong>{" "}
                      {transaction.sellerNames || "N/A"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const renderTaxInfo = () => (
    <Accordion type="single" collapsible className="border rounded-lg">
      <AccordionItem value="tax-info">
        <AccordionTrigger className="px-4 sm:px-6 hover:no-underline">
          <div className="flex items-center gap-2">
            <AssessmentIcon className="h-5 w-5 text-primary" />
            <h3 className="text-base sm:text-lg font-bold">
              Tax Information ({property.taxAssessments?.length || 0} years)
            </h3>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-2 sm:px-4">
          {property.taxAssessments?.slice(0, 5).map((assessment, index) => (
            <Card
              key={assessment.id || index}
              className="mb-3 sm:mb-4 bg-muted/50 last:mb-0"
            >
              <CardContent className="p-4 sm:p-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Year</p>
                    <p className="text-base sm:text-xl font-semibold">
                      {assessment.year}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tax Amount</p>
                    <p className="text-sm sm:text-base">
                      {formatCurrency(assessment.taxAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tax Rate</p>
                    <p className="text-sm sm:text-base">
                      {assessment.taxRate
                        ? `${(assessment.taxRate * 100).toFixed(2)}%`
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Assessed Value
                    </p>
                    <p className="text-sm sm:text-base">
                      {formatCurrency(assessment.assessedValue)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const renderStatusIndicators = () => (
    <Card className="mb-3 sm:mb-4">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <GavelIcon className="h-5 w-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">Property Status</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={property.onMarket ? "default" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Market: {property.onMarket ? "On Market" : "Off Market"}
          </Badge>
          <Badge
            variant={property.vacant ? "destructive" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Vacant: {property.vacant ? "Yes" : "No"}
          </Badge>
          <Badge
            variant={property.distressed ? "destructive" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Distressed: {property.distressed ? "Yes" : "No"}
          </Badge>
          <Badge
            variant={property.preForeclosure ? "destructive" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Pre-Foreclosure: {property.preForeclosure ? "Yes" : "No"}
          </Badge>
          <Badge
            variant={property.bankruptcy ? "destructive" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Bankruptcy: {property.bankruptcy ? "Yes" : "No"}
          </Badge>
          <Badge
            variant={property.taxDelinquency ? "destructive" : "secondary"}
            className="text-xs sm:text-sm"
          >
            Tax Delinquency: {property.taxDelinquency ? "Yes" : "No"}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  const renderLocationInfo = () => (
    <Card className="mb-3 sm:mb-4">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <LocationIcon className="h-5 w-5 text-primary" />
          <h3 className="text-base sm:text-lg font-bold">Location Details</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <p className="text-sm text-muted-foreground">City</p>
            <p className="text-sm sm:text-base">{property.city || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">County</p>
            <p className="text-sm sm:text-base">{property.county || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">State</p>
            <p className="text-sm sm:text-base">{property.state || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">ZIP Code</p>
            <p className="text-sm sm:text-base">{property.zip || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">School District</p>
            <p className="text-sm sm:text-base">
              {property.schoolDistrict || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Flood Zone</p>
            <p className="text-sm sm:text-base">
              {property.floodZoneDescription || "N/A"}
            </p>
          </div>
          <div className="col-span-full">
            <p className="text-sm text-muted-foreground">Coordinates</p>
            <p className="text-sm sm:text-base">
              {property.latitude && property.longitude
                ? `${property.latitude.toFixed(
                    4
                  )}, ${property.longitude.toFixed(4)}`
                : "N/A"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderListingInfo = () => {
    const listings = property.listings;

    // Prefer resolved primary listing (latest dated listing with valid amount fallback)
    const activeListing = resolvePrimaryListing(property);

    if (!activeListing && (!listings || listings.length === 0)) return null;

    return (
      <>
        {/* Current/Active Listing */}
        {activeListing && (
          <Card className="mb-3 sm:mb-4">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <HomeIcon className="h-5 w-5 text-primary" />
                <h3 className="text-base sm:text-lg font-bold">
                  {activeListing.status === "Active"
                    ? "Current Listing"
                    : "Latest Listing"}{" "}
                  Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">List Price</p>
                  <p className="text-lg sm:text-xl font-semibold text-primary">
                    {formatCurrency(activeListing.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge
                    variant={
                      activeListing.status === "Active"
                        ? "default"
                        : activeListing.status === "Sold"
                        ? "secondary"
                        : "outline"
                    }
                    className="mt-1 text-xs sm:text-sm"
                  >
                    {activeListing.status || "N/A"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Days on Market
                  </p>
                  <p className="text-sm sm:text-base">
                    {activeListing.daysOnMarket ||
                      property.daysOnMarket ||
                      "N/A"}{" "}
                    days
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Listing Date</p>
                  <p className="text-sm sm:text-base">
                    {formatDate(activeListing.listingDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">MLS Number</p>
                  <p className="text-sm sm:text-base">
                    {activeListing.mlsNumber || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Price Per Sq Ft
                  </p>
                  <p className="text-sm sm:text-base">
                    {activeListing.pricePerSquareFoot
                      ? `$${activeListing.pricePerSquareFoot}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Agent Name</p>
                  <p className="text-sm sm:text-base">
                    {activeListing.agentName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Office</p>
                  <p className="text-sm sm:text-base">
                    {activeListing.officeName || "N/A"}
                  </p>
                </div>
                {activeListing && activeListing.interiorFeatures && (
                  <div className="col-span-full">
                    <p className="text-sm text-muted-foreground">
                      Interior Features
                    </p>
                    <p className="text-sm sm:text-base">
                      {activeListing.interiorFeatures}
                    </p>
                  </div>
                )}
                {activeListing && activeListing.appliances && (
                  <div className="col-span-full">
                    <p className="text-sm text-muted-foreground">Appliances</p>
                    <p className="text-sm sm:text-base">
                      {activeListing.appliances}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Listing History */}
        {listings && listings.length > 0 && (
          <Accordion type="single" collapsible className="border rounded-lg">
            <AccordionItem value="listing-history">
              <AccordionTrigger className="px-4 sm:px-6 hover:no-underline">
                <div className="flex items-center gap-2">
                  <HistoryIcon className="h-5 w-5 text-primary" />
                  <h3 className="text-base sm:text-lg font-bold">
                    Listing History ({listings.length} listings)
                  </h3>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 sm:px-4">
                {listings.slice(0, 10).map((listing, index) => (
                  <Card
                    key={listing.id || index}
                    className={cn(
                      "mb-3 sm:mb-4 last:mb-0",
                      listing.status === "Active"
                        ? "bg-green-50/50"
                        : "bg-muted/50"
                    )}
                  >
                    <CardContent className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            List Price
                          </p>
                          <p className="text-base sm:text-lg font-semibold text-primary">
                            {formatCurrency(listing.amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Status
                          </p>
                          <Badge
                            variant={
                              listing.status === "Active"
                                ? "default"
                                : listing.status === "Sold"
                                ? "secondary"
                                : "outline"
                            }
                            className="mt-1 text-xs sm:text-sm"
                          >
                            {listing.status || "N/A"}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Days on Market
                          </p>
                          <p className="text-sm sm:text-base">
                            {listing.daysOnMarket || "N/A"} days
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Listing Date
                          </p>
                          <p className="text-sm sm:text-base">
                            {formatDate(listing.listingDate)}
                          </p>
                        </div>
                        <div className="col-span-full sm:col-span-2 lg:col-span-1">
                          <p className="text-sm text-muted-foreground">
                            MLS Number
                          </p>
                          <p className="text-sm sm:text-base">
                            {listing.mlsNumber || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Price Per Sq Ft
                          </p>
                          <p className="text-sm sm:text-base">
                            {listing.pricePerSquareFoot
                              ? `$${listing.pricePerSquareFoot}`
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Agent</p>
                          <p className="text-sm sm:text-base">
                            {listing.agentName || "N/A"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {listings.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    Showing 10 of {listings.length} listings
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </>
    );
  };

  const renderLoanInfo = () => {
    if (!property.openLoans?.length && !property.loanAmount) return null;

    return (
      <Card className="mb-3 sm:mb-4">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <GavelIcon className="h-5 w-5 text-primary" />
            <h3 className="text-base sm:text-lg font-bold">
              Mortgage Information
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Total Loan Balance
              </p>
              <p className="text-sm sm:text-base">
                {formatCurrency(
                  property.loanBalance || property.openLoanAmount
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Loan to Value Ratio
              </p>
              <p className="text-sm sm:text-base">
                {property.loanToValueRatio
                  ? `${(property.loanToValueRatio * 100).toFixed(1)}%`
                  : "N/A"}
              </p>
            </div>
            {property.openLoans?.[0] && (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Interest Rate</p>
                  <p className="text-sm sm:text-base">
                    {property.openLoans[0].rate
                      ? `${(property.openLoans[0].rate * 100).toFixed(2)}%`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Monthly Payment
                  </p>
                  <p className="text-sm sm:text-base">
                    {formatCurrency(
                      property.openLoans[0].estimatedPayment ||
                        property.loanPaymentAmount
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Loan Type</p>
                  <p className="text-sm sm:text-base">
                    {property.openLoans[0].loanType || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lender</p>
                  <p className="text-sm sm:text-base">
                    {property.openLoans[0].lenderName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className="text-sm sm:text-base">
                    {formatDate(property.openLoans[0].dueDate)}
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className={cn(
          "max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0",
          isMobile && "h-screen max-h-screen rounded-none m-0"
        )}
      >
        <DialogHeader className="sticky top-0 z-10 bg-primary text-white p-4 sm:p-6">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 pr-2">
              <DialogTitle className="text-lg sm:text-2xl font-bold leading-tight break-words">
                Property Details
              </DialogTitle>
              <p className="text-sm sm:text-base opacity-90 mt-1">
                {property.streetAddress}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/10 shrink-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="p-2 sm:p-6">
          {renderBasicInfo()}
          {renderValuationInfo()}
          {renderListingInfo()}
          {renderLoanInfo()}
          {renderStatusIndicators()}
          {renderOwnershipInfo()}
          {renderLocationInfo()}
          <div className="mt-4">{renderSalesHistory()}</div>
          <div className="mt-4">{renderTaxInfo()}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDetailsModal;
